import { useQuery } from '@/lib/api-hooks';
import { backendApi } from '@/lib/backend';

export type ModuleLevel = 'off' | 'view' | 'manage';

export type TopLevelModule =
  | 'dashboard'
  | 'cms'
  | 'reporting'
  | 'extensions'
  | 'trunks'
  | 'ivrs'
  | 'routes'
  | 'phones'
  | 'chat'
  | 'auditLog';

type SessionModuleAccess = {
  modules: Record<TopLevelModule, ModuleLevel>;
  dashboardWidgets: Record<string, boolean>;
};

type SessionContext = {
  user: {
    id: string;
    email: string;
    name: string;
    role: 'admin' | 'technician' | 'customer';
  };
  customer: { id: string; name: string; status: 'active' | 'disabled' } | null;
  moduleAccess: SessionModuleAccess;
};

function moduleLevelAtLeast(level: ModuleLevel, required: 'view' | 'manage') {
  if (level === 'off') return false;
  if (required === 'view') return level === 'view' || level === 'manage';
  return level === 'manage';
}

function canViewModule(access: SessionModuleAccess, moduleId: TopLevelModule) {
  return moduleLevelAtLeast(access.modules[moduleId] ?? 'off', 'view');
}

function canManageModule(access: SessionModuleAccess, moduleId: TopLevelModule) {
  return moduleLevelAtLeast(access.modules[moduleId] ?? 'off', 'manage');
}

export function useModuleAccess(moduleId: TopLevelModule) {
  const session = useQuery(backendApi.tenancy.getSessionContext, {}) as
    | SessionContext
    | null
    | undefined;
  const isStaff = session?.user.role === 'admin' || session?.user.role === 'technician';
  const access = session?.moduleAccess;

  return {
    session,
    isStaff,
    level: access?.modules[moduleId] ?? ('off' as ModuleLevel),
    canView: isStaff || (access ? canViewModule(access, moduleId) : false),
    canManage: isStaff || (access ? canManageModule(access, moduleId) : false),
    loading: session === undefined,
  };
}